import { UnreachableCaseError } from '@errors/unreachable';
import { CurrentSite } from '@snap-types/actions/current-site';
import type { SellingUnitOfMeasure } from '@snap-types/models/enums';
import { setIsHydratingAction } from '@ui/features/configurator/store/actions/set-is-hydrating';
import { toggleFieldEditableAction } from '@ui/features/configurator/store/actions/toggle-field-editable';
import { serializeConfiguratorSnapshot } from '@ui/features/configurator/store/helpers/serialize-configurator-snapshot';
import type {
  Configuration,
  ConfiguratorStore,
  ConfiguratorStoreAction,
} from '@ui/features/configurator/types';
import { setAutoFreeze } from 'immer';
import { createStore } from 'zustand';
import type { DevtoolsOptions } from 'zustand/middleware';
import { devtools } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';

import {
  addOperationAction,
  createOperationAction,
} from './actions/add-operation';
import { attachOperationDocumentAction } from './actions/attach-document';
import { moveOperationAction } from './actions/move-operation';
import { removeOperationDocumentAction } from './actions/remove-document';
import { removeOperationAction } from './actions/remove-operation';
import { setFieldErrorAction } from './actions/set-field-error';
import { updateFieldAction } from './actions/update-field';
import { calculateCosts } from './helpers/calculate-costs';
import { checkFormValidity } from './helpers/check-form-validity';
import { generateDescription } from './helpers/generate-description';
import { initializeConfiguratorStore } from './helpers/initialize-configurator-store';
import { resolveDependencies } from './helpers/resolve-dependencies';
import { serializeConfigurationState } from './helpers/serialize';

setAutoFreeze(false);

// recommended: install Redux devtools extension for your browser
// https://github.com/pmndrs/zustand?tab=readme-ov-file#redux-devtools
const storeDevToolsOptions: DevtoolsOptions = {
  enabled: import.meta.env.DEV,
  anonymousActionType: 'unknown',
};

// note: the redux devtool expects to log one action at a time.
// the stringified array is only necessary with this custom array dispatch
const zustandActionsToDevToolsLog = (actions: ConfiguratorStoreAction[]) => {
  return actions.length === 1
    ? actions[0]
    : actions.map((a) => `${a.type} ${JSON.stringify(a.payload)}`).join('----');
};

/**
 * Creates a store for a configurator provider.
 * We are using the immer middleware for managing complex and immutable state
 *
 * @docs https://docs.pmnd.rs/zustand/integrations/immer-middleware
 */
export function createConfiguratorStore(
  site: Pick<CurrentSite, 'code' | 'name'>,
  incomingConfiguration: Configuration,
  sellingUnitOfMeasure: SellingUnitOfMeasure,
) {
  const { configuration, fields, blueprint, costs, isHydrating } =
    initializeConfiguratorStore(incomingConfiguration);

  const configuratorStore = createStore<ConfiguratorStore>()(
    devtools(
      immer((set, get) => ({
        site,
        configuration,
        fields,
        blueprint,
        costs,
        sellingUnitOfMeasure,
        isHydrating,

        dispatch: (actionOrActions) => {
          const actions = Array.isArray(actionOrActions)
            ? actionOrActions
            : [actionOrActions];
          const devToolsLoggedAction = zustandActionsToDevToolsLog(actions);
          return set(
            (state) => {
              const previousState = get();

              let forceCalculation = false;

              for (const action of actions) {
                switch (action.type) {
                  case 'addOperation':
                    addOperationAction(state, action.payload.operation);
                    break;

                  case 'attachDocument':
                    attachOperationDocumentAction(
                      state,
                      action.payload.document,
                      action.payload.stepId,
                    );
                    break;

                  case 'createOperation':
                    createOperationAction(state, action.payload.operation);
                    break;

                  case 'moveOperation':
                    moveOperationAction(
                      state,
                      action.payload.stepKey,
                      action.payload.index,
                    );
                    break;

                  case 'removeDocument':
                    removeOperationDocumentAction(
                      state,
                      action.payload.documentUuid,
                      action.payload.stepId,
                    );
                    break;

                  case 'removeOperation':
                    removeOperationAction(state, action.payload.stepId);
                    forceCalculation = true;
                    break;

                  case 'updateField':
                    updateFieldAction(
                      state,
                      action.payload.fieldKey,
                      action.payload.value,
                    );
                    break;

                  case 'setFieldError':
                    setFieldErrorAction(
                      state,
                      action.payload.fieldKey,
                      action.payload.error,
                    );
                    break;

                  case 'toggleFieldEditable':
                    toggleFieldEditableAction(
                      state,
                      action.payload.fieldKey,
                      action.payload.isEditable,
                    );
                    break;

                  case 'setIsHydrating':
                    setIsHydratingAction(state, action.payload.isHydrating);
                    break;

                  default: {
                    throw new UnreachableCaseError(action);
                  }
                }
              }

              resolveDependencies(state, previousState.fields, {
                preserveFieldValues: state.isHydrating,
              });
              calculateCosts(state, previousState.fields, forceCalculation);
              generateDescription(state);
            },
            undefined,
            devToolsLoggedAction,
          );
        },

        checkFormValidity: () => {
          return checkFormValidity(get());
        },

        serialize: () => {
          return serializeConfigurationState(get());
        },

        saveConfiguratorSnapshot: () => {
          const { blueprint, fields } = get();
          const savedPart = serializeConfiguratorSnapshot(blueprint, fields);
          return savedPart;
        },
      })),
      storeDevToolsOptions,
    ),
  );

  return configuratorStore;
}
